import { ExternalE2EEKeyProvider, isE2EESupported } from "livekit-client";
/**
 * End-to-end encryption for LiveKit calls (frame-level, Insertable Streams).
 * The SFU only ever sees encrypted frames — even our own server cannot
 * decrypt the media.
 *
 * The passphrase is derived from the v2 chat key (which itself comes from a
 * wallet EIP-191 signature — see chatCrypto.ts) with a distinct domain
 * prefix, so chat and call keys never coincide but share the same trust
 * root: only wallet owners (and guests handed the key via ECDH) can derive
 * it. The legacy address-derived scheme is gone — without the chat key the
 * call entry is blocked until the key is available; never downgrade media.
 */
/** True when the browser supports frame-level E2EE (Insertable Streams). */
export function callE2EESupported() {
    try {
        return isE2EESupported();
    }
    catch {
        return false;
    }
}
const CALL_E2EE_DOMAIN = "dwdata-call-e2ee-v2|";
export const E2EE_KEY_UNAVAILABLE_ERROR = "e2ee-key-unavailable";
export const E2EE_UNSUPPORTED_ERROR = "e2ee-unsupported";
export const E2EE_SETUP_FAILED_ERROR = "e2ee-setup-failed";
/**
 * Domain-separated call passphrase from the raw v2 chat key bytes;
 * ExternalE2EEKeyProvider runs PBKDF2 on it. Deterministic across devices
 * because the v2 chat key itself is (RFC 6979 personal_sign).
 */
export async function deriveCallE2EEPassphrase(chatKeyBytes) {
    const domain = new TextEncoder().encode(CALL_E2EE_DOMAIN);
    const material = new Uint8Array(domain.length + chatKeyBytes.length);
    material.set(domain, 0);
    material.set(chatKeyBytes, domain.length);
    const hash = await crypto.subtle.digest("SHA-256", material);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}
/**
 * Build keyed E2EE options or block entry explicitly. In particular, a new
 * guest cannot publish clear media while waiting for an authorized owner.
 * Guests already holding the key remain able to continue or rejoin.
 */
export async function createCallE2EE(getChatKeyBytes) {
    if (!callE2EESupported())
        throw new Error(E2EE_UNSUPPORTED_ERROR);
    const chatKey = await getChatKeyBytes();
    if (!chatKey)
        throw new Error(E2EE_KEY_UNAVAILABLE_ERROR);
    const keyProvider = new ExternalE2EEKeyProvider();
    await keyProvider.setKey(await deriveCallE2EEPassphrase(chatKey));
    const worker = new Worker(new URL("livekit-client/e2ee-worker", import.meta.url));
    return { keyProvider, worker, hasKey: !!chatKey };
}
/** Set (or update) the call passphrase on an existing provider — used for
 *  the retroactive mid-call upgrade when the chat key arrives late. */
export async function setCallE2EEKey(keyProvider, chatKeyBytes) {
    await keyProvider.setKey(await deriveCallE2EEPassphrase(chatKeyBytes));
}
